/**
 * Adapted from PlayCanvas Engine's gsplat-octree.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import * as THREE from 'three'
import type {
  GSplatLoadSource,
  GSplatLoadUrlObject,
  GSplatParserContext,
  IGSplatParser,
} from '@/plugins/gsplat/loaders/GSplatHandler'
import { GSplatAssetLoader } from '@/plugins/gsplat/unified/octree/GSplatAssetLoader'
import type { GSplatAssetLoaderBase } from '@/plugins/gsplat/unified/octree/GSplatAssetLoaderBase'
import {
  GSplatOctree,
  type GSplatOctreeFileResource,
  type GSplatOctreeManifest,
} from '@/plugins/gsplat/unified/octree/GSplatOctree'
import { GSplatOctreeResource } from '@/plugins/gsplat/unified/resource/GSplatOctreeResource'

interface GSplatJsonResponse {
  readonly ok: boolean
  readonly status: number
  readonly statusText: string
  json(): Promise<unknown>
}

export interface GSplatOctreeParserOptions<
  TResource extends GSplatOctreeFileResource,
  TParserOptions = unknown,
> {
  readonly maxRetries?: number
  readonly maxConcurrentLoads?: number
  readonly fetch?: (input: string, init: RequestInit) => Promise<GSplatJsonResponse>
  /** 默认递归使用同一个 GSplatHandler；测试或宿主也可注入 Resource factory。 */
  readonly loadResource?: (
    url: string,
    signal: AbortSignal,
    attempt: number,
    parserOptions: TParserOptions | undefined
  ) => Promise<TResource | undefined>
  readonly createAssetLoader?: (options: {
    readonly context: GSplatParserContext<TParserOptions>
    readonly maxConcurrentLoads: number
    readonly maxRetries: number
  }) => GSplatAssetLoaderBase<TResource>
  readonly createResource?: (options: {
    readonly assetFileUrl: string
    readonly metadata: GSplatOctreeManifest
    readonly octree: GSplatOctree<TResource>
    readonly aabb: THREE.Box3
  }) => GSplatOctreeResource<GSplatOctreeManifest>
}

/** 精确识别 `lod-meta.json` 并以 loader/resource 两阶段发布的后端无关 parser。 */
export class GSplatOctreeParser<
  TResource extends GSplatOctreeFileResource,
  TParserOptions = unknown,
> implements IGSplatParser<GSplatOctreeResource<GSplatOctreeManifest>, TParserOptions>
{
  private readonly options: GSplatOctreeParserOptions<TResource, TParserOptions>

  constructor(options: GSplatOctreeParserOptions<TResource, TParserOptions> = {}) {
    this.options = options
    this.validateCount(options.maxRetries ?? 0, 'maxRetries', 0)
    this.validateCount(options.maxConcurrentLoads ?? 2, 'maxConcurrentLoads', 1)
  }

  canParse(context: GSplatParserContext<TParserOptions>): boolean {
    return context.basename === 'lod-meta.json'
  }

  async load(
    url: GSplatLoadUrlObject,
    context: GSplatParserContext<TParserOptions>
  ): Promise<GSplatOctreeResource<GSplatOctreeManifest>> {
    const maxRetries = this.options.maxRetries ?? context.handler.maxRetries
    this.validateCount(maxRetries, 'maxRetries', 0)
    const metadata = await this.loadJson(url.load, url.original, context.signal, maxRetries)
    const aabb = this.createRootAabb(metadata, url.original)
    this.throwIfAborted(context.signal, url.original)

    const maxConcurrentLoads = this.options.maxConcurrentLoads ?? 2
    const loader =
      this.options.createAssetLoader?.({ context, maxConcurrentLoads, maxRetries }) ??
      new GSplatAssetLoader<TResource>({
        maxConcurrentLoads,
        maxRetries,
        canLoad: () => !context.handler.isDisposed,
        loadResource: (resourceUrl, signal, attempt) =>
          this.loadFileResource(resourceUrl, signal, attempt, context),
      })

    let octree: GSplatOctree<TResource> | null = null
    let resource: GSplatOctreeResource<GSplatOctreeManifest> | null = null
    try {
      octree = new GSplatOctree<TResource>(context.assetFileUrl, metadata, { loader })
      this.throwIfAborted(context.signal, url.original)
      resource =
        this.options.createResource?.({
          assetFileUrl: context.assetFileUrl,
          metadata,
          octree,
          aabb,
        }) ?? new GSplatOctreeResource({ aabb, metadata, octree })
      this.throwIfAborted(context.signal, url.original)
      return resource
    } catch (error) {
      if (resource) resource.dispose()
      else if (octree) octree.dispose()
      else loader.destroy()
      throw error
    }
  }

  private async loadJson(
    source: GSplatLoadSource,
    original: string,
    signal: AbortSignal | undefined,
    maxRetries: number
  ): Promise<GSplatOctreeManifest> {
    let lastError: Error | null = null
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      this.throwIfAborted(signal, original)
      try {
        const data =
          source instanceof Blob
            ? JSON.parse(await source.text())
            : await this.fetchJson(source, signal)
        this.throwIfAborted(signal, original)
        return this.assertManifest(data, original)
      } catch (cause) {
        if (signal?.aborted) throw createAbortError(original)
        lastError = cause instanceof Error ? cause : new Error(String(cause))
      }
    }
    throw new Error(
      `加载 GSplat Octree 失败: ${original} [${lastError?.message ?? 'unknown error'}]`,
      lastError ? { cause: lastError } : undefined
    )
  }

  private async fetchJson(source: string, signal?: AbortSignal): Promise<unknown> {
    const fetchImpl = this.options.fetch ?? globalThis.fetch
    if (!fetchImpl) throw new Error('当前环境不支持 fetch')
    const init: RequestInit = signal ? { signal } : {}
    const response = await fetchImpl(source, init)
    if (!response.ok) {
      throw new Error(`HTTP ${response.status} ${response.statusText}`)
    }
    return response.json()
  }

  private async loadFileResource(
    url: string,
    signal: AbortSignal,
    attempt: number,
    context: GSplatParserContext<TParserOptions>
  ): Promise<TResource | undefined> {
    const result = this.options.loadResource
      ? await this.options.loadResource(url, signal, attempt, context.options)
      : await context.handler.load(url, {
          signal,
          assetFileUrl: url,
          ...(context.options !== undefined ? { parserOptions: context.options } : {}),
        })
    if (result === undefined) return undefined
    if (!isOctreeFileResource(result)) {
      throw new Error(`GSplat Octree file parser 未返回可释放 Resource: ${url}`)
    }
    return result as TResource
  }

  private assertManifest(data: unknown, original: string): GSplatOctreeManifest {
    if (!data || typeof data !== 'object') {
      throw new Error(`GSplat Octree metadata 不是对象: ${original}`)
    }
    const manifest = data as Partial<GSplatOctreeManifest>
    if (!Array.isArray(manifest.filenames) || !manifest.tree) {
      throw new Error(`GSplat Octree metadata 缺少 filenames/tree: ${original}`)
    }
    if (!Number.isSafeInteger(manifest.lodLevels) || Number(manifest.lodLevels) < 1) {
      throw new Error(`GSplat Octree metadata lodLevels 非法: ${original}`)
    }
    for (const [index, filename] of manifest.filenames.entries()) {
      if (typeof filename !== 'string' || filename.length === 0) {
        throw new Error(`GSplat Octree metadata filename 非法: ${original}, index=${index}`)
      }
    }
    return data as GSplatOctreeManifest
  }

  private createRootAabb(metadata: GSplatOctreeManifest, original: string): THREE.Box3 {
    const bound = metadata.tree.bound
    if (!bound || bound.min.length !== 3 || bound.max.length !== 3) {
      throw new Error(`GSplat Octree metadata 根节点缺少 bound: ${original}`)
    }
    for (const value of [...bound.min, ...bound.max]) {
      if (!Number.isFinite(value)) {
        throw new Error(`GSplat Octree metadata 根节点 bound 必须为有限数: ${original}`)
      }
    }
    for (let axis = 0; axis < 3; axis++) {
      if (bound.min[axis] > bound.max[axis]) {
        throw new Error(`GSplat Octree metadata 根节点 bound min 不能大于 max: ${original}`)
      }
    }
    return new THREE.Box3(new THREE.Vector3(...bound.min), new THREE.Vector3(...bound.max))
  }

  private throwIfAborted(signal: AbortSignal | undefined, url: string): void {
    if (signal?.aborted) throw createAbortError(url)
  }

  private validateCount(value: number, label: string, minimum: number): void {
    if (!Number.isSafeInteger(value) || value < minimum) {
      throw new Error(`非法 GSplatOctreeParser ${label}: ${value}`)
    }
  }
}

function isOctreeFileResource(value: unknown): value is GSplatOctreeFileResource {
  return (
    !!value &&
    typeof value === 'object' &&
    typeof (value as { dispose?: unknown }).dispose === 'function'
  )
}

function createAbortError(url: string): DOMException {
  return new DOMException(`GSplat Octree load 已取消: ${url}`, 'AbortError')
}
